import { Graphics, IDestroyOptions, Renderer, Sprite, filters } from "pixi.js"
import { findRendererApp } from "./renderer_app"

/**
 * Graphics shown through a blur that is baked once, after drawing, instead of filtered every frame.
 *
 * A live BlurFilter costs several offscreen passes and render-target switches per frame, and every
 * visible storey carries its own cues. The artwork changes only on a full map render, so soften()
 * renders it through the blur into a texture at that point, outside the frame's filter stack.
 * Baking lazily inside the frame (cacheAsBitmap) is unsafe here: pixi 6 sends a nested filter's
 * output to the enclosing filter's target, and the world surface sits inside the sight mask.
 *
 * The bake itself waits for the next animation frame. The same filtered render-to-texture made from
 * inside the game's frame update (where soften() is called) comes out EMPTY in pixi 6.2 -- measured:
 * 0 opaque pixels there, 33155 for the identical bake between frames. Until it lands (and headless,
 * with no renderer), the drawing shows through a live blur, so no frame goes without it.
 */
export class SoftenedGraphics extends Graphics {
    readonly blur: InstanceType<typeof filters.BlurFilter>
    private baked?: Sprite
    private pendingBake?: number

    constructor(strength: number, quality: number) {
        super()
        this.blur = new filters.BlurFilter(strength, quality)
    }

    /** Call after drawing. */
    soften(): void {
        this.discardBake()
        if (!this.geometry.graphicsData.length) return
        this.filters = [this.blur]
        const renderer = findRendererApp()?.renderer as Renderer | undefined
        if (!renderer || typeof requestAnimationFrame === "undefined") return
        this.pendingBake = requestAnimationFrame(() => {
            this.pendingBake = undefined
            if (!this.destroyed) this.bake(renderer)
        })
    }

    clear(): this {
        this.discardBake()
        return super.clear()
    }

    destroy(options?: IDestroyOptions | boolean): void {
        this.discardBake()
        this.blur.destroy()
        super.destroy(options)
    }

    protected _render(renderer: Renderer): void {
        // Once baked, only the softened child is drawn.
        if (!this.baked) super._render(renderer)
    }

    private bake(renderer: Renderer): void {
        // generateTexture keeps the world transform of a parented object, which would bake the camera in.
        const brush = new Graphics(this.geometry)
        brush.filters = [this.blur]
        const region = brush.getLocalBounds().clone().pad(this.blur.padding)
        region.ceil()
        const texture = renderer.generateTexture(brush, { region, resolution: this.blur.resolution })
        brush.filters = null
        brush.destroy()
        this.filters = null
        this.baked = new Sprite(texture)
        this.baked.position.set(region.x, region.y)
        this.addChild(this.baked)
    }

    private discardBake(): void {
        if (this.pendingBake !== undefined) {
            cancelAnimationFrame(this.pendingBake)
            this.pendingBake = undefined
        }
        this.filters = null
        if (!this.baked) return
        this.baked.destroy({ texture: true, baseTexture: true })
        this.baked = undefined
    }
}
